import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Stethoscope, BookOpen, ShieldCheck, Mail, GraduationCap, Briefcase } from "lucide-react";
import SeoHead from "@/components/SeoHead";
import { ARTICLES, ARTICLE_GROUPS, articleUrl } from "@/lib/seoArticles";
import { useAllArticles } from "@/lib/useAllArticles";

/**
 * /authors/ashlee-skabla-velez
 *
 * Author page for Memory Lane's medical lead. Purpose is twofold:
 *   1. Give Google a real, crawlable Person page with credentials so the
 *      Article schema's `author.url` resolves — a core E-E-A-T signal for
 *      medical content (Google's "Your Money or Your Life" guidelines
 *      weigh author expertise heavily for health topics).
 *   2. Give a real human face to caregivers landing on an article —
 *      shows who wrote / clinically reviewed what they're reading.
 *
 * Schema emitted: a single Person + ProfilePage JSON-LD graph. The Article
 * pages reference this page via `author.url`.
 */
export const AUTHOR_NAME = "Ashlee Skabla Velez, APRN, ACNPC-AG";
export const AUTHOR_URL = "https://memorylanecare.net/authors/ashlee-skabla-velez";
export const AUTHOR_PHOTO = "https://customer-assets.emergentagent.com/job_memory-support-guide/artifacts/vqmtf97v_Screenshot%202026-03-08%20at%208.23.50%E2%80%AFPM.png";

const CREDENTIALS = [
    { label: "APRN", full: "Advanced Practice Registered Nurse", Icon: Stethoscope },
    { label: "ACNPC-AG", full: "Acute Care Nurse Practitioner — Adult-Gerontology (board certified)", Icon: Award },
];

const EXPERTISE = [
    "Dementia & Alzheimer's care across the full disease trajectory",
    "CMS GUIDE Model (Guiding an Improved Dementia Experience)",
    "Hospice & end-of-life care",
    "Acute geriatric care",
    "Family caregiver education & coaching",
    "Care planning, advance directives, and family meetings",
];

export default function AuthorAshlee() {
    // List of articles authored on the platform. Memory Lane's entire SEO
    // library is written or clinically reviewed by Ashlee, so we surface
    // every article here — both for caregivers browsing her work and for
    // Google to validate the author-content link. Includes Content Studio
    // articles automatically (via useAllArticles).
    const authoredArticles = useAllArticles();
    // Group the articles by their section for an easier scan.
    const byGroup = {};
    for (const a of authoredArticles) {
        if (!byGroup[a.group]) byGroup[a.group] = [];
        byGroup[a.group].push(a);
    }

    const jsonLd = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "ProfilePage",
                "@id": AUTHOR_URL,
                mainEntity: { "@id": `${AUTHOR_URL}#person` },
            },
            {
                "@type": "Person",
                "@id": `${AUTHOR_URL}#person`,
                name: AUTHOR_NAME,
                givenName: "Ashlee",
                familyName: "Skabla Velez",
                honorificSuffix: "APRN, ACNPC-AG",
                jobTitle: "Nurse Practitioner & Founder",
                description:
                    "Board-certified Acute Care Nurse Practitioner — Adult-Gerontology, with clinical experience in hospice, dementia care, and the CMS GUIDE Model. Founder of Memory Lane Care Navigator.",
                image: AUTHOR_PHOTO,
                url: AUTHOR_URL,
                worksFor: {
                    "@type": "Organization",
                    name: "Memory Lane Care",
                    url: "https://memorylanecare.net/",
                },
                hasCredential: [
                    {
                        "@type": "EducationalOccupationalCredential",
                        credentialCategory: "Professional license",
                        name: "APRN — Advanced Practice Registered Nurse",
                    },
                    {
                        "@type": "EducationalOccupationalCredential",
                        credentialCategory: "Board certification",
                        name: "ACNPC-AG — Acute Care Nurse Practitioner, Adult-Gerontology",
                    },
                ],
                knowsAbout: EXPERTISE,
            },
        ],
    };

    return (
        <div className="min-h-screen bg-cream" data-testid="author-page">
            <SeoHead
                title={`${AUTHOR_NAME} — Memory Lane Care`}
                description={`Board-certified Nurse Practitioner specializing in dementia, hospice, and the CMS GUIDE Model. Founder and clinical author at Memory Lane Care.`}
                canonicalPath="/authors/ashlee-skabla-velez"
                articleData={jsonLd}
            />

            {/* Breadcrumb */}
            <nav
                aria-label="Breadcrumb"
                data-testid="author-breadcrumbs"
                className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 text-sm text-[#4C5551]"
            >
                <ol className="flex items-center gap-1.5 flex-wrap">
                    <li><Link to="/" className="hover:text-teal-dark">Memory Lane</Link></li>
                    <li aria-hidden="true">›</li>
                    <li>Authors</li>
                    <li aria-hidden="true">›</li>
                    <li className="text-[#1F2624] font-medium">Ashlee Skabla Velez, APRN, ACNPC-AG</li>
                </ol>
            </nav>

            {/* Hero */}
            <header className="max-w-4xl mx-auto px-4 sm:px-6 pt-8 md:pt-12 pb-6">
                <div className="grid md:grid-cols-[180px_1fr] gap-6 md:gap-8 items-start">
                    <div>
                        <img
                            src={AUTHOR_PHOTO}
                            alt={`Portrait of ${AUTHOR_NAME}, founder of Memory Lane Care`}
                            className="w-36 h-36 md:w-44 md:h-44 rounded-3xl object-cover shadow-md ring-2 ring-white"
                            data-testid="author-photo"
                            loading="eager"
                        />
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">Clinical lead &amp; founder</p>
                        <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-[#12776F] mt-1 leading-tight" data-testid="author-name">
                            Ashlee Skabla Velez
                        </h1>
                        <p className="font-heading text-lg md:text-xl text-[#4C5551] mt-1 leading-snug" data-testid="author-credentials">
                            APRN, ACNPC-AG
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2" data-testid="author-credential-pills">
                            {CREDENTIALS.map(({ label, full, Icon }) => (
                                <span
                                    key={label}
                                    title={full}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-2xl bg-teal-soft text-teal-dark text-xs font-semibold"
                                >
                                    <Icon size={12} strokeWidth={2} /> {label}
                                </span>
                            ))}
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-2xl bg-cream-dark text-[#4C5551] text-xs font-semibold">
                                <Briefcase size={12} strokeWidth={2} /> Founder, Memory Lane Care
                            </span>
                        </div>
                    </div>
                </div>
            </header>

            {/* Bio */}
            <section className="max-w-3xl mx-auto px-4 sm:px-6 py-4 md:py-6" data-testid="author-bio">
                <p className="text-base md:text-lg text-[#1F2624] leading-relaxed">
                    Ashlee is a board-certified Acute Care Nurse Practitioner — Adult-Gerontology
                    with deep clinical experience in <strong>hospice</strong>, <strong>dementia care</strong>,
                    and the <strong>CMS GUIDE Model</strong>. She started Memory Lane after watching too
                    many families navigate dementia alone — figuring out diagnosis, behavior, benefits,
                    and end-of-life decisions with no single place to return to.
                </p>
                <p className="text-base md:text-lg text-[#1F2624] leading-relaxed mt-4">
                    Every article on Memory Lane Care is <strong>written or clinically reviewed</strong> by
                    Ashlee. The goal is to give caregivers the same information she gives families at the
                    bedside — in plain language, with concrete next steps, and grounded in current
                    clinical guidance.
                </p>
            </section>

            {/* Credentials breakdown */}
            <section className="max-w-3xl mx-auto px-4 sm:px-6 py-4 md:py-6">
                <h2 className="font-heading text-2xl text-[#12776F] mb-3 flex items-center gap-2">
                    <GraduationCap size={20} strokeWidth={1.8} className="text-teal-dark" />
                    Credentials &amp; licensure
                </h2>
                <ul className="space-y-2" data-testid="author-credential-list">
                    {CREDENTIALS.map(({ label, full, Icon }) => (
                        <li
                            key={label}
                            className="ml-card p-4 flex items-start gap-3"
                        >
                            <span className="w-10 h-10 rounded-2xl bg-teal-soft text-teal-dark inline-flex items-center justify-center shrink-0">
                                <Icon size={18} strokeWidth={1.7} />
                            </span>
                            <div>
                                <p className="font-heading text-lg text-[#1F2624]">{label}</p>
                                <p className="text-sm text-[#4C5551] leading-relaxed">{full}</p>
                            </div>
                        </li>
                    ))}
                </ul>
                <p className="text-xs text-[#6E7A74] mt-3">
                    Licensure verifiable through state board of nursing on request.
                </p>
            </section>

            {/* Areas of expertise */}
            <section className="max-w-3xl mx-auto px-4 sm:px-6 py-4 md:py-6">
                <h2 className="font-heading text-2xl text-[#12776F] mb-3 flex items-center gap-2">
                    <ShieldCheck size={20} strokeWidth={1.8} className="text-teal-dark" />
                    Areas of expertise
                </h2>
                <ul className="grid sm:grid-cols-2 gap-2" data-testid="author-expertise-list">
                    {EXPERTISE.map((e) => (
                        <li key={e} className="flex items-start gap-2 text-[15px] text-[#1F2624]">
                            <span aria-hidden="true" className="text-teal-dark mt-0.5">•</span>
                            <span>{e}</span>
                        </li>
                    ))}
                </ul>
            </section>

            {/* Articles by group */}
            <section className="max-w-3xl mx-auto px-4 sm:px-6 py-6 md:py-10" data-testid="author-articles">
                <h2 className="font-heading text-2xl text-[#12776F] mb-2 flex items-center gap-2">
                    <BookOpen size={20} strokeWidth={1.8} className="text-teal-dark" />
                    Articles by Ashlee
                </h2>
                <p className="text-sm text-[#4C5551] mb-5">
                    {authoredArticles.length} clinical articles written or reviewed by Ashlee.
                </p>
                <div className="space-y-6">
                    {Object.entries(byGroup).map(([groupKey, items]) => {
                        const meta = ARTICLE_GROUPS[groupKey];
                        if (!meta) return null;
                        return (
                            <div key={groupKey} data-testid={`author-articles-group-${groupKey}`}>
                                <h3 className="text-xs uppercase tracking-wider font-semibold text-teal-dark mb-2">
                                    {meta.label}
                                </h3>
                                <ul className="space-y-1.5">
                                    {items.map((a) => (
                                        <li key={a.slug}>
                                            <Link
                                                to={articleUrl(a)}
                                                data-testid={`author-article-${groupKey}-${a.slug}`}
                                                className="group flex items-start gap-2 py-2 text-[15px] text-[#1F2624] hover:text-teal-dark"
                                            >
                                                <ArrowRight
                                                    size={14}
                                                    strokeWidth={2}
                                                    className="mt-1 text-teal-dark/60 group-hover:translate-x-0.5 transition-transform shrink-0"
                                                />
                                                <span className="underline decoration-cream-dark group-hover:decoration-teal-dark">
                                                    {a.h1}
                                                </span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* CTA */}
            <section className="max-w-3xl mx-auto px-4 sm:px-6 py-8 md:py-12">
                <div className="ml-card p-6 md:p-7 bg-gradient-to-br from-teal-soft/40 to-cream-light text-center" data-testid="author-cta">
                    <h2 className="font-heading text-2xl text-[#12776F]">
                        Want Ashlee to walk you through it?
                    </h2>
                    <p className="text-sm md:text-base text-[#4C5551] mt-2 max-w-xl mx-auto">
                        Book a free 15-minute walk-through with a Nurse Practitioner — no obligation,
                        no sales pitch, just clarity on what to do next for your family.
                    </p>
                    <div className="mt-5 flex flex-wrap justify-center gap-3">
                        <Link
                            to="/?book=1#landing-tabs"
                            data-testid="author-cta-book"
                            className="ml-btn-primary inline-flex items-center gap-2"
                        >
                            Book a free 15-min walk-through <ArrowRight size={16} strokeWidth={1.8} />
                        </Link>
                        <a
                            href="mailto:ashlee@asvmedicalservices.com"
                            data-testid="author-cta-email"
                            className="ml-btn-secondary inline-flex items-center gap-2"
                        >
                            <Mail size={16} strokeWidth={1.8} /> Email Ashlee
                        </a>
                    </div>
                </div>
            </section>

            {/* Editorial standards */}
            <section className="max-w-3xl mx-auto px-4 sm:px-6 py-4 md:py-6 pb-12">
                <h2 className="font-heading text-xl text-[#1F2624] mb-2">Editorial standards</h2>
                <ul className="text-sm text-[#4C5551] space-y-1.5 leading-relaxed">
                    <li>Every article is written or clinically reviewed by Ashlee Skabla Velez, APRN, ACNPC-AG.</li>
                    <li>Clinical guidance is grounded in current CMS, NIA, Alzheimer&apos;s Association, and peer-reviewed sources.</li>
                    <li>Articles are dated and updated when guidance changes.</li>
                    <li>Memory Lane is in beta and not yet HIPAA-compliant — content is educational, not a substitute for clinical care.</li>
                </ul>
            </section>
        </div>
    );
}
